export interface JourneyStep {
  num: string
  title: string
  desc: string
}
export interface MvpPath {
  name: string
  desc: string
}
export interface EbookBlock {
  /** Número visual de la tarjeta: '01' … '06'. */
  num: string
  /** Etiqueta corta del bloque. */
  stage: string
  title: string
  /** Resultado concreto en una línea. */
  outcome: string
  /** Cosas concretas que la persona puede aplicar. */
  points: string[]
}
export interface RecipeCategory {
  title: string
  desc: string
}
export interface Bono {
  num: string
  name: string
  desc: string
  cover?: string
}
export interface BoxIdea {
  title: string
  desc: string
}
export interface Faq {
  q: string
  a: string
}

/**
 * Formato monetario único de la landing: siempre "ARS 00.000".
 */
export const PRICE = {
  previous: 'ARS 24.990',
  current: 'ARS 17.990',
  note: 'Compra única · Acceso digital inmediato',
} as const

export const PRODUCT_FACTS: string[] = [
  'Ebook de 145 páginas',
  '60 recetas comerciales',
  'Método M.V.P.™',
  '12 boxes y combos',
  '5 bonos incluidos',
]

/** Situaciones cotidianas en las que la compradora tiene que reconocerse. */
export const PAIN_BLOCKS: string[] = [
  'Te gusta cocinar y querés generar un ingreso desde casa, pero no sabés qué productos conviene ofrecer.',
  'Tenés cientos de recetas guardadas en el celular y todavía no empezaste con ninguna.',
  'No sabés cuánto cobrar: ponés un precio "a ojo" y después no sabés si te quedó ganancia.',
  'Tenés miedo de gastar de más en ingredientes y que después no se venda.',
  'No sabés cómo organizar la producción cuando entran varios pedidos juntos.',
  'Dudás de la conservación y del traslado: no querés que el postre llegue arruinado.',
  'No sabés cómo presentar lo que hacés para que se vea rico y valga lo que pedís.',
  'Sentís que "todavía te falta algo" antes de animarte a salir a vender.',
]

/** El recorrido práctico del sistema. */
export const JOURNEY_STEPS: JourneyStep[] = [
  {
    num: '01',
    title: 'Elegís qué vender',
    desc: 'Con el Método M.V.P.™ armás tu Menú Vendible de Partida: alrededor de 5 productos estratégicos para empezar, en lugar de intentar hacer todo.',
  },
  {
    num: '02',
    title: 'Organizás tu producción',
    desc: 'Compras inteligentes, orden de cocina, producción por tandas y conservación para que preparar deje de ser un caos.',
  },
  {
    num: '03',
    title: 'Preparás productos comerciales',
    desc: '60 recetas seleccionadas por su facilidad, rendimiento, conservación y traslado, no sólo por su sabor.',
  },
  {
    num: '04',
    title: 'Calculás y presentás',
    desc: 'Costeo básico, packaging y formatos de venta para saber cuánto cobrar y que tu producto se vea como algo que se compra.',
  },
  {
    num: '05',
    title: 'Salís al mercado',
    desc: 'Plan de implementación de 7 días y bonos de aplicación para pasar del libro a ofrecer tus primeros postres.',
  },
]

/** Criterios con los que el Método M.V.P.™ selecciona los productos iniciales. */
export const MVP_CRITERIA: string[] = [
  'Inversión inicial',
  'Facilidad de preparación',
  'Ingredientes compartidos',
  'Producción',
  'Conservación',
  'Traslado',
  'Atractivo visual',
  'Potencial comercial',
]

/** Los tres caminos del Método M.V.P.™ */
export const MVP_PATHS: MvpPath[] = [
  {
    name: 'MVP Económico',
    desc: 'Para arrancar con la menor inversión posible, aprovechando ingredientes compartidos y equipamiento que ya tenés.',
  },
  {
    name: 'MVP Sin Complicaciones',
    desc: 'Para quien quiere procesos simples: pocas técnicas, tiempos cortos y productos que perdonan errores.',
  },
  {
    name: 'MVP Alto Valor',
    desc: 'Para apuntar a productos de mayor valor percibido, mejor presentación y ticket más alto por pedido.',
  },
]

/** Qué incluye el ebook. */
export const EBOOK_BLOCKS: EbookBlock[] = [
  {
    num: '01',
    stage: 'Selección',
    title: 'Método M.V.P.™',
    outcome: 'Definir con criterio los productos con los que vas a empezar, sin intentar fabricar todo.',
    points: [
      'Los 8 criterios para elegir tus productos iniciales',
      'Tres caminos: Económico, Sin Complicaciones y Alto Valor',
      'Tu Menú Vendible de Partida armado antes de comprar nada',
    ],
  },
  {
    num: '02',
    stage: 'Recetario',
    title: '60 recetas comerciales',
    outcome:
      'Recetas elegidas para producir, conservar, trasladar y vender, no sólo para cocinar un domingo.',
    points: [
      'Brownies, cookies, alfajores y bocados',
      'Cuadrados, porciones, tartas, postres en vaso y budines',
      'Rendimiento y comportamiento pensados para la venta',
    ],
  },
  {
    num: '03',
    stage: 'Producción',
    title: 'Cocina, compras y tandas',
    outcome: 'Que preparar varios pedidos deje de depender de la improvisación.',
    points: [
      'Organización de la cocina hogareña',
      'Compras inteligentes y uso compartido de insumos',
      'Producción por tandas, conservación y traslado',
    ],
  },
  {
    num: '04',
    stage: 'Números',
    title: 'Costeo y presentación',
    outcome: 'Saber cuánto te cuesta producir y a qué precio tiene sentido vender.',
    points: [
      'Costeo básico paso a paso',
      'Packaging y presentación del producto',
      'Formatos de venta: unidad, porción, combo',
    ],
  },
  {
    num: '05',
    stage: 'Valor',
    title: '12 boxes y combos',
    outcome: 'Combinar lo que ya sabés hacer para aumentar el valor percibido de cada pedido.',
    points: [
      'Armados listos para ocasiones y fechas',
      'Cómo combinar productos que comparten insumos',
      'Ideas de presentación para cada box',
    ],
  },
  {
    num: '06',
    stage: 'Acción',
    title: 'Plan de 7 días',
    outcome: 'Una hoja de ruta para pasar del libro a estar lista para empezar a ofrecer.',
    points: [
      'Qué hacer cada día, en orden',
      'Prueba de producción antes de vender',
      'Tu menú, tus precios y tus fotos listos',
    ],
  },
]

/** Cómo están agrupadas las 60 recetas. */
export const RECIPE_CATEGORIES: RecipeCategory[] = [
  {
    title: 'Brownies, cookies, alfajores y bocados',
    desc: 'Productos de alta rotación, fáciles de trasladar y de vender por unidad o por caja.',
  },
  {
    title: 'Cuadrados, porciones y tartas',
    desc: 'Formatos que rinden por tanda y se venden por porción con buena presentación.',
  },
  {
    title: 'Postres en vaso',
    desc: 'Individuales, prolijos y con muy buen atractivo visual para pedidos y combos.',
  },
  {
    title: 'Budines y muffins',
    desc: 'Larga conservación, producción simple e ideales para arrancar sin equipamiento.',
  },
  {
    title: 'Productos premium',
    desc: 'Opciones de mayor valor percibido para subir el ticket cuando ya tenés práctica.',
  },
]

export const EBOOK_FEATURES: string[] = [
  'Aproximadamente 145 páginas, altamente visual',
  'Escrito en lenguaje simple, para principiantes',
  'Pensado para cocina hogareña y utensilios accesibles',
  'Recetas con foco en producción y venta',
  'Checklists y planillas para completar',
  'Se lee desde el celular, la tablet o la computadora',
]

/** Ideas de boxes y combos. */
export const BOX_IDEAS: BoxIdea[] = [
  {
    title: 'Box para regalar',
    desc: 'Un armado prolijo con packaging cuidado que se vende solo como obsequio.',
  },
  {
    title: 'Combo para compartir',
    desc: 'Varias unidades de tus productos más simples, con mejor precio por pedido.',
  },
  {
    title: 'Box degustación',
    desc: 'Porciones chicas de distintos productos para que te prueben por primera vez.',
  },
  {
    title: 'Combo merienda',
    desc: 'Productos que comparten insumos y se producen en la misma tanda.',
  },
  {
    title: 'Box de fecha especial',
    desc: 'Armados para ocasiones puntuales, cuando la demanda se concentra.',
  },
  {
    title: 'Box premium',
    desc: 'Tu producto de mayor valor, presentado para justificar un ticket más alto.',
  },
]

/** Los 5 bonos incluidos en la compra. */
export const BONOS: Bono[] = [
  {
    num: '01',
    name: 'Calculadora de Costos Dinámica™',
    desc: 'Para saber cuánto te cuesta producir cada postre y qué precio tiene sentido cobrar.',
    cover: 'https://cdn.magicpatterns.com/patterns/generated-images/1ea62d41-d3dc-49c1-af37-be3433048f71.jpg',
  },
  {
    num: '02',
    name: 'Cero Desperdicio™',
    desc: 'Para organizar insumos, conservación y freezer, y reducir pérdidas en cada tanda.',
    cover: 'https://cdn.magicpatterns.com/patterns/generated-images/a381c1f6-ba38-4870-bc62-42b56df5f511.jpg',
  },
  {
    num: '03',
    name: 'Menú que Vende™',
    desc: 'Para convertir los productos elegidos en una oferta clara, atractiva y fácil de comprar.',
    cover: 'https://cdn.magicpatterns.com/patterns/generated-images/6cefbb7e-fd40-408f-b2c1-847fef51c99c.jpg',
  },
  {
    num: '04',
    name: 'Fotos que Dan Hambre™',
    desc: 'Para mejorar las fotos de tus postres usando solamente el celular y luz natural.',
    cover: 'https://cdn.magicpatterns.com/patterns/generated-images/4de4c27e-6d66-4b2e-9492-4bfa1ef4327b.jpg',
  },
  {
    num: '05',
    name: 'Tus Primeras 20 Ventas™',
    desc: 'Plan práctico con WhatsApp, contactos cercanos, mensajes y acciones para conseguir compradores.',
    cover: 'https://cdn.magicpatterns.com/patterns/generated-images/b6628463-f06e-4a2b-b139-0f2cadd31474.jpg',
  },
]

/** Comparación: mega pack tradicional vs. el sistema. */
export const COMPARE_OLD: string[] = [
  'Cientos de recetas sin orden',
  'Información acumulada',
  'No sabés con cuál empezar',
  'Sin sistema de producción',
  'Sin camino hacia la venta',
]

export const COMPARE_NEW: string[] = [
  '60 recetas seleccionadas',
  'Menú Vendible de Partida',
  'Organización y producción',
  'Costeo y presentación',
  'Plan concreto de implementación',
]

export const TRANSFORM_BEFORE: string[] = [
  'Guardás recetas y nunca empezás',
  'No sabés qué conviene ofrecer',
  'Ponés precios a ojo',
  'Comprás ingredientes de más',
  'La producción se te desordena',
  'No sabés cómo mostrar lo que hacés',
]

export const TRANSFORM_AFTER: string[] = [
  'Tenés 5 productos elegidos con criterio',
  'Sabés exactamente qué comprar',
  'Calculás costos y precios',
  'Producís por tandas y conservás bien',
  'Presentás con packaging y fotos',
  'Tenés un plan para buscar tus primeras ventas',
]

export const AUDIENCE_POINTS: string[] = [
  'Querés generar un ingreso extra desde casa.',
  'Disfrutás la repostería pero no sos profesional.',
  'Querés empezar con tu cocina hogareña, tal como está.',
  'Necesitás un sistema simple, no más información suelta.',
  'No querés invertir en equipamiento profesional.',
  'Tenés recetas pero nunca las convertiste en una oferta.',
  'Querés empezar con pocos productos bien elegidos.',
]

/** Lo que NO hace falta para empezar. */
export const NOT_NEEDED: string[] = [
  'Experiencia comercial',
  'Conocimientos de marketing',
  'Cocina profesional',
  'Equipamiento industrial',
  'Local a la calle',
  'Formación en pastelería',
]

export const OFFER_INCLUDES: string[] = [
  'Ebook premium de aproximadamente 145 páginas',
  '60 recetas comerciales seleccionadas',
  'Método M.V.P.™ — Menú Vendible de Partida',
  'Organización de cocina, compras y producción por tandas',
  'Conservación, traslado, packaging y costeo básico',
  '12 boxes y combos',
  'Plan de implementación de 7 días',
  'Los 5 bonos incluidos',
]

export const FAQS: Faq[] = [
  {
    q: '¿Necesito saber pastelería?',
    a: 'No. Está pensado para principiantes y para personas con conocimientos básicos. Las recetas y los procesos se explican paso a paso, en lenguaje simple.',
  },
  {
    q: '¿Necesito equipamiento profesional?',
    a: 'No. Todo el sistema está orientado a una cocina hogareña y a herramientas accesibles. El Método M.V.P.™ incluso te ayuda a elegir productos según lo que ya tenés.',
  },
  {
    q: '¿Son solamente recetas?',
    a: 'No. Las recetas son una parte del sistema. También incluye selección de menú, organización de cocina, compras, producción por tandas, conservación, traslado, costeo, packaging, boxes y plan de acción.',
  },
  {
    q: '¿Cuántas recetas incluye?',
    a: '60 recetas comerciales organizadas en distintas categorías: brownies, cookies, alfajores y bocados; cuadrados, porciones y tartas; postres en vaso; budines y muffins; y productos premium.',
  },
  {
    q: '¿Cómo recibo el producto?',
    a: 'En formato digital, inmediatamente después de la compra. Podés leerlo desde el celular, la tablet o la computadora.',
  },
  {
    q: '¿Puedo usarlo si todavía no vendo nada?',
    a: 'Sí. De hecho, está diseñado especialmente para ayudarte a organizar el comienzo: qué preparar, cómo producirlo, cuánto cobrar y qué hacer para buscar tus primeras ventas.',
  },
  {
    q: '¿Los bonos están incluidos?',
    a: 'Sí. Los 5 bonos forman parte de la compra, sin costo adicional.',
  },
]
